import { Button, Heading, Modal, Select, Status, TSelectOption } from "@r360/library";
import moment from "moment";
import React, { useEffect, useState } from "react";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import { requestLoadingSelector } from "../../Selectors";
import { accountSelector, checkoutCartSelector, clientDataSelector } from "../../selectors/Selectors";
import { fetchReservation, fetchReservations } from "../../store/actions/account";
import { createCartFromReservation } from "../../store/actions/checkoutCart";
import { TCartItem, TReservation } from "../../store/types";
import useTranslate from "../../hooks/useTranslate";

type TMergeBookingsModal = {
    products: TCartItem[];
};

type TMergingStatus = "idle" | "inProgress" | "complete" | "error";

export const MergeBookingsModal = ({ products }: TMergeBookingsModal) => {
    const t = useTranslate();
    const dispatch = useAppDispatch();
    const account = useAppSelector(accountSelector);
    const userToken = account.token;
    const { createdFromReservation } = useAppSelector(checkoutCartSelector);
    const reservations = account.reservations;
    const clientData = useAppSelector(clientDataSelector);
    const reservationTypes = clientData.reservationtypes;
    const createCartFromReservationInProgress = useAppSelector(state =>
        requestLoadingSelector(state, "createCartFromReservation")
    );
    const fetchReservationsInProgress = useAppSelector(state => requestLoadingSelector(state, "fetchReservations"));
    const fetchReservationInProgress = useAppSelector(state => requestLoadingSelector(state, "fetchReservation"));

    const [bookingsAvailableForMerge, setBookingsAvailableForMerge] = useState<TReservation[]>([]);
    const [selectedBookingToMerge, setSelectedBookingToMerge] = useState<TReservation>();
    const [showModal, setShowModal] = useState(false);
    const [choiceMade, setChoiceMade] = useState(false);
    const [mergingStatus, setMergingStatus] = useState<TMergingStatus>("idle");

    // Fetch reservations.
    useEffect(() => {
        dispatch(fetchReservations(userToken));
    }, [dispatch, userToken]);

    const handleMatchingBookings = async (bookings: TReservation[]) => {
        const bookingsWithAllowRestore: TReservation[] = [];

        for (const booking of bookings) {
            if (booking.allowrestore) {
                bookingsWithAllowRestore.push(booking);
            } else if (booking.allowrestore === false) {
                continue;
            } else {
                const fullBooking: TReservation = await dispatch(fetchReservation(userToken, booking.resvid));
                if (fullBooking.allowrestore) {
                    bookingsWithAllowRestore.push(fullBooking);
                }
            }
        }

        if (bookingsWithAllowRestore.length === 0) {
            return;
        }

        setBookingsAvailableForMerge(bookingsWithAllowRestore);
        setSelectedBookingToMerge(bookingsWithAllowRestore[0]);
        setShowModal(true);
    };

    // Find bookings within the same date span.
    useEffect(() => {
        if (!fetchReservationsInProgress && reservations.length > 0 && !choiceMade && !createdFromReservation) {
            const matchingBookings = reservations.filter(reservation => {
                if (reservation.cancelled || reservation.reservationrules.allowmodify === false) {
                    return false;
                }

                return products.some(product => {
                    return moment(product.arrdate).isBetween(reservation.arrdate, reservation.depdate, undefined, "[]");
                });
            });

            if (matchingBookings.length === 0) {
                return;
            }

            handleMatchingBookings(matchingBookings);
        }
        // eslint-disable-next-line
    }, [fetchReservationsInProgress]);

    // Set merging complete when the bookings are merged.
    useEffect(() => {
        if (createdFromReservation && !createCartFromReservationInProgress && choiceMade && showModal) {
            setMergingStatus("complete");
        }
    }, [createdFromReservation, createCartFromReservationInProgress, choiceMade, showModal]);

    // Close the modal when merging is complete.
    useEffect(() => {
        if (mergingStatus === "complete") {
            setTimeout(() => {
                setShowModal(false);
            }, 3000);
        }
    }, [mergingStatus]);

    // Do an early return if there are no bookings available for merge.
    if (bookingsAvailableForMerge.length === 0) {
        return null;
    }

    const mutlipleAvailableBookings = bookingsAvailableForMerge.length > 1;
    const title = mutlipleAvailableBookings ? t("book.merge_bookings.title_multiple") : t("book.merge_bookings.title");

    const description = mutlipleAvailableBookings
        ? t("book.merge_bookings.description_multiple")
        : t("book.merge_bookings.description");

    const renderBookingsAvailableForMerge = () => {
        const getLabel = (booking: TReservation) => {
            return t("book.merge_bookings.booking_label", [booking.resvid, booking.arrdate]);
        };

        const handleSelectChange = (option: TSelectOption) => {
            const selectedBooking = bookingsAvailableForMerge.find(booking => booking.resvid === option.value);
            setSelectedBookingToMerge(selectedBooking);
        };

        if (mutlipleAvailableBookings) {
            const selectOptions: TSelectOption[] = bookingsAvailableForMerge.map(booking => {
                return {
                    value: booking.resvid,
                    label: getLabel(booking),
                };
            });

            return (
                <Select
                    options={selectOptions}
                    onSelectCallback={handleSelectChange}
                    fullWidth
                    disabled={createCartFromReservationInProgress}
                />
            );
        }

        return <span className="u-fw-medium">{getLabel(bookingsAvailableForMerge[0])}</span>;
    };

    const handleMergeBookings = () => {
        setChoiceMade(true);
        setMergingStatus("inProgress");

        dispatch(
            createCartFromReservation(
                selectedBookingToMerge?.resvid,
                userToken,
                reservationTypes,
                clientData?.cookie,
                true // Merge bookings.
            )
        );
    };

    const handleDismissModal = () => {
        setShowModal(false);
        setChoiceMade(true);
    };

    const confirmButtonLoading =
        mergingStatus === "inProgress" || fetchReservationsInProgress || fetchReservationInProgress;

    return (
        <>
            <Modal
                open={showModal}
                onClose={handleDismissModal}
                closeBtn={false}
                closeOnOutsideClick={false}
                closeOnEscape={false}
                overflowVisible
            >
                {mergingStatus === "complete" ? (
                    <div className="u-d-flex u-flex-column u-gap-12 u-align-items-center u-py-24">
                        <Status type="success" size={20} />
                        <span>{t("book.merge_bookings.completed", [selectedBookingToMerge?.resvid ?? ""])}</span>
                    </div>
                ) : (
                    <>
                        <Heading type="h2" styleAs="h3" className="u-mb-12">
                            {title}
                        </Heading>
                        <p className="u-mb-24">{description}</p>
                        {renderBookingsAvailableForMerge()}
                        <div className="u-d-flex u-justify-content-end u-gap-12 u-mt-36">
                            <Button
                                type="secondary"
                                onClick={handleDismissModal}
                                disabled={mergingStatus === "inProgress"}
                            >
                                {t("book.merge_bookings.cancel")}
                            </Button>
                            <Button onClick={handleMergeBookings} loading={confirmButtonLoading}>
                                {t("book.merge_booking.ok")}
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
        </>
    );
};
